import React, { useState } from 'react';
import { Check, Star } from 'lucide-react';
import { services } from '../data/mock';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import QuoteModal from './QuoteModal';

const Services = () => {
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);

  const handleReservation = () => {
    setIsQuoteOpen(true);
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="mb-4 bg-cyan-100 text-primary hover:bg-cyan-200">
            Nos Solutions Publicitaires
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Choisissez votre format publicitaire
          </h2>
          <p className="text-xl text-gray-600">
            Des solutions adaptées à tous les budgets pour maximiser l'impact de vos campagnes
          </p>
        </div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {services.map((service) => (
            <Card 
              key={service.id} 
              className={`relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${
                service.popular ? 'border-primary border-2' : ''
              }`}
            >
              {service.popular && (
                <div className="absolute top-4 right-4 z-10">
                  <Badge className="bg-primary text-white">
                    <Star size={14} className="mr-1" aria-hidden="true" />
                    Populaire
                  </Badge>
                </div>
              )}

              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={service.image}
                  alt={`${service.name} - panneaux publicitaires SESAM`}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white">{service.name}</h3>
                </div>
              </div>

              <CardHeader>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-4xl font-bold text-primary">{service.price}</span>
                  <span className="text-gray-600">{service.period}</span>
                </div>
                <CardDescription className="text-base">{service.capacity}</CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <p className="text-gray-600">{service.description}</p>
                
                <ul className="space-y-3" role="list">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="mt-1 flex-shrink-0">
                        <div className="bg-green-100 rounded-full p-1">
                          <Check size={14} className="text-green-600" aria-hidden="true" />
                        </div>
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button 
                  onClick={handleReservation}
                  className={`w-full ${
                    service.popular 
                      ? 'bg-primary hover:bg-primary/90' 
                      : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  Réserver maintenant
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Besoin d'une solution personnalisée ?
          </h3>
          <p className="text-gray-600 mb-6">
            Notre équipe est à votre disposition pour étudier vos besoins spécifiques et vous proposer une offre sur mesure.
          </p>
          <Button 
            onClick={scrollToContact}
            size="lg"
            className="bg-primary hover:bg-primary/90"
          >
            Contactez-nous
          </Button>
        </div>
      </div>

      {/* Modal de réservation */}
      <QuoteModal 
        isOpen={isQuoteOpen}
        onClose={() => setIsQuoteOpen(false)}
      />
    </section>
  );
};

export default Services;
